import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { useTheme } from "@mui/material/styles";
import { AccessTime, Add, CalendarMonth, CheckCircle, TrendingUp } from "@mui/icons-material";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { fetchAdminBookingListJson, ADMIN_BOOKINGS_MAX_PAGE_SIZE } from "@/lib/queryClient";
import { useAdminServicesCatalog, useAdminStaffCatalog } from "@/hooks/useAdminPagedCatalogs";
import { formatCents, getStatusChipColor, sameEntityId } from "@/lib/utils";
import {
  normalizeBookingsList,
  bookingToEditPreset,
  resolveOfferingName,
  resolveStaffDisplayName,
  type NewBookingDrawerPreset,
} from "@/lib/adminApi";
import BookingDetailModal from "@/components/BookingDetailModal";
import RechartsChartTooltip from "@/components/RechartsChartTooltip";
import {
  assignOverlapLanes,
  CALENDAR_EVENT_INSET_PX,
  CALENDAR_LANE_GAP_PX,
} from "@/lib/calendarOverlapLayout";
import {
  calendarBookingEventBaseSx,
  calendarBookingSubtitleSx,
  calendarBookingTitleSx,
  staffPaletteKeyFromIndex,
} from "@/lib/calendarEventCardStyles";
import NewBookingDrawer from "@/components/NewBookingDrawer";
import type { BookingWithDetails } from "@shared/schema";

const CHART_DAYS = 30;
const TIMELINE_START_HOUR = 8;
const TIMELINE_END_HOUR = 19;
const PX_PER_HOUR = 44;

type Period = "today" | "week" | "month" | "all";

function getPeriodRange(period: Period): { start: Date; end: Date } {
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  if (period === "today") {
    return { start, end };
  }
  if (period === "week") {
    const s = new Date(start);
    s.setDate(s.getDate() - 6);
    return { start: s, end };
  }
  if (period === "month") {
    const s = new Date(start);
    s.setDate(s.getDate() - 29);
    return { start: s, end };
  }
  return { start: new Date(0), end };
}

function filterBookingsInPeriod(bookings: BookingWithDetails[], period: Period): BookingWithDetails[] {
  const { start, end } = getPeriodRange(period);
  const t0 = start.getTime();
  const t1 = end.getTime();
  return bookings.filter((b) => {
    if (b.status === "cancelled") return false;
    const t = new Date(b.startAt).getTime();
    return t >= t0 && t <= t1;
  });
}

function revenueCentsInBookings(bookings: BookingWithDetails[]): number {
  return bookings
    .filter((b) => b.status === "confirmed" || b.status === "completed")
    .reduce((sum, b) => sum + (b.service?.priceCents || 0), 0);
}

function buildLast30DaysChart(
  bookings: BookingWithDetails[],
  mode: "revenue" | "bookings",
): { date: string; label: string; value: number }[] {
  const end = new Date();
  end.setHours(12, 0, 0, 0);
  const rows: { date: string; label: string; value: number; revenue: number; count: number }[] = [];
  for (let i = CHART_DAYS - 1; i >= 0; i--) {
    const d = new Date(end);
    d.setDate(d.getDate() - i);
    const iso = d.toISOString().slice(0, 10);
    rows.push({
      date: iso,
      label: String(d.getDate()),
      value: 0,
      revenue: 0,
      count: 0,
    });
  }
  for (const b of bookings) {
    if (b.status === "cancelled") continue;
    const iso = new Date(b.startAt).toISOString().slice(0, 10);
    const row = rows.find((r) => r.date === iso);
    if (!row) continue;
    row.count += 1;
    if (b.status === "confirmed" || b.status === "completed") {
      row.revenue += b.service?.priceCents || 0;
    }
  }
  return rows.map((r) => ({
    date: r.date,
    label: r.label,
    value: mode === "revenue" ? r.revenue : r.count,
  }));
}

function StatCard({
  title,
  value,
  sub,
  icon: Icon,
  color,
}: {
  title: string;
  value: string | number;
  sub?: string;
  icon: typeof TrendingUp;
  color: "primary" | "secondary" | "success" | "info";
}) {
  return (
    <Card variant="outlined">
      <CardContent sx={{ py: 2.5 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              {title}
            </Typography>
            <Typography variant="h5" fontWeight="bold" sx={{ mt: 0.5 }}>
              {value}
            </Typography>
            {sub && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                {sub}
              </Typography>
            )}
          </Box>
          <Icon color={color} sx={{ mt: 0.5 }} />
        </Box>
      </CardContent>
    </Card>
  );
}

export default function AdminDashboard() {
  const { t } = useTranslation();
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const [period, setPeriod] = useState<Period>("month");
  const [chartMode, setChartMode] = useState<"revenue" | "bookings">("revenue");
  const [newBookingOpen, setNewBookingOpen] = useState(false);
  const [newBookingPreset, setNewBookingPreset] = useState<NewBookingDrawerPreset | undefined>(undefined);
  const [selectedBooking, setSelectedBooking] = useState<BookingWithDetails | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const { data: bookings, isLoading: loadingBookings } = useQuery({
    queryKey: ["/api/admin/bookings", "list", 1, ADMIN_BOOKINGS_MAX_PAGE_SIZE],
    queryFn: () => fetchAdminBookingListJson(1, ADMIN_BOOKINGS_MAX_PAGE_SIZE),
    select: (data: unknown) => normalizeBookingsList(data) as BookingWithDetails[],
  });

  const { staff: staffList } = useAdminStaffCatalog();
  const { services: servicesCatalog } = useAdminServicesCatalog();

  const displayBookings = useMemo((): BookingWithDetails[] | undefined => {
    if (loadingBookings) return undefined;
    return bookings ?? [];
  }, [bookings, loadingBookings]);

  const currency = displayBookings?.[0]?.currency || "GBP";

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const todaysBookings =
    displayBookings?.filter((b) => {
      const start = new Date(b.startAt);
      return start >= today && start < tomorrow && b.status !== "cancelled";
    }) || [];

  const todayScheduleLayout = useMemo(() => assignOverlapLanes(todaysBookings), [todaysBookings]);

  const upcomingBookings =
    displayBookings
      ?.filter((b) => {
        const start = new Date(b.startAt);
        return start >= new Date() && b.status !== "cancelled";
      })
      .sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime())
      .slice(0, 5) || [];

  const inPeriod = useMemo(
    () => (displayBookings ? filterBookingsInPeriod(displayBookings, period) : []),
    [displayBookings, period],
  );

  const totalRevenueCents = revenueCentsInBookings(inPeriod);
  const totalBookingsCount = inPeriod.length;
  const avgCents =
    totalBookingsCount > 0 ? Math.round(totalRevenueCents / totalBookingsCount) : 0;

  const chartData = useMemo(
    () => (displayBookings ? buildLast30DaysChart(displayBookings, chartMode) : []),
    [displayBookings, chartMode],
  );

  const todayDateLine = new Date().toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "short",
  });

  const timelineMinsTotal = (TIMELINE_END_HOUR - TIMELINE_START_HOUR) * 60;
  const trackHeight = (TIMELINE_END_HOUR - TIMELINE_START_HOUR) * PX_PER_HOUR;

  const yAxisMoney = (v: number) =>
    `${currency === "GBP" ? "£" : currency === "EUR" ? "€" : "$"}${(v / 100).toFixed(0)}`;

  const tickInterval = 5;

  const axisTick = useMemo(() => ({ fontSize: 11, fill: theme.palette.text.secondary }), [theme]);

  return (
    <Box sx={{ p: 3, maxWidth: 1280, mx: "auto" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight="bold" data-testid="text-dashboard-title">
            {t("dashboard.title")}
          </Typography>
          <Typography color="text.secondary">{t("dashboard.subtitleOverview")}</Typography>
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setNewBookingOpen(true)}
            data-testid="button-dashboard-new-booking"
          >
            {t("dashboard.newBooking")}
          </Button>
          <ToggleButtonGroup
            value={period}
            exclusive
            size="small"
            onChange={(_, v: Period | null) => v && setPeriod(v)}
            aria-label={t("dashboard.title")}
            data-testid="toggle-dashboard-period"
          >
            <ToggleButton value="today">{t("dashboard.period.today")}</ToggleButton>
            <ToggleButton value="week">{t("dashboard.period.week")}</ToggleButton>
            <ToggleButton value="month">{t("dashboard.period.month")}</ToggleButton>
            <ToggleButton value="all">{t("dashboard.period.all")}</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" },
          gap: 2,
          mb: 3,
        }}
      >
        {loadingBookings ? (
          [1, 2, 3, 4].map((i) => <Skeleton key={i} variant="rounded" height={112} />)
        ) : (
          <>
            <StatCard
              title={t("dashboard.stats.revenueBooked")}
              value={formatCents(totalRevenueCents, currency)}
              icon={TrendingUp}
              color="primary"
            />
            <StatCard
              title={t("dashboard.stats.totalBookings")}
              value={totalBookingsCount}
              icon={CalendarMonth}
              color="secondary"
            />
            <StatCard
              title={t("dashboard.stats.avgBookingValue")}
              value={formatCents(avgCents, currency)}
              icon={InsightsOutlinedIcon}
              color="success"
            />
            <StatCard
              title={t("dashboard.stats.todaysBookings")}
              value={todaysBookings.length}
              sub={t("dashboard.todayBookingsDateLine", { date: todayDateLine })}
              icon={CheckCircle}
              color="info"
            />
          </>
        )}
      </Box>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardHeader
          title={t("dashboard.revenueTrend")}
          subheader={t("dashboard.revenueTrendSub")}
          action={
            <ToggleButtonGroup
              value={chartMode}
              exclusive
              size="small"
              onChange={(_, v: "revenue" | "bookings" | null) => v && setChartMode(v)}
              data-testid="toggle-dashboard-chart-mode"
            >
              <ToggleButton value="revenue">{t("dashboard.chartToggleRevenue")}</ToggleButton>
              <ToggleButton value="bookings">{t("dashboard.chartToggleBookings")}</ToggleButton>
            </ToggleButtonGroup>
          }
          titleTypographyProps={{ variant: "subtitle1", fontWeight: 600 }}
        />
        <CardContent>
          {loadingBookings ? (
            <Skeleton variant="rounded" height={220} />
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                <XAxis dataKey="label" tick={axisTick} interval={tickInterval} />
                <YAxis
                  tick={axisTick}
                  allowDecimals={chartMode === "bookings" ? false : undefined}
                  tickFormatter={(v: number) =>
                    chartMode === "revenue" ? yAxisMoney(v) : String(v)
                  }
                />
                <Tooltip
                  content={RechartsChartTooltip}
                  formatter={(v: number) =>
                    chartMode === "revenue"
                      ? [formatCents(v, currency), t("reports.chartRevenue")]
                      : [v, t("reports.chartBookings")]
                  }
                />
                <Bar dataKey="value" fill={primary} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "repeat(2, 1fr)" }, gap: 3 }}>
        <Box>
          <Card variant="outlined">
            <CardHeader
              title={t("dashboard.todayScheduleTitle")}
              subheader={
                loadingBookings
                  ? undefined
                  : t("dashboard.todayScheduleSubHeader", { count: todaysBookings.length })
              }
              action={<CheckCircle fontSize="small" color="action" />}
              titleTypographyProps={{ variant: "subtitle1", fontWeight: 600 }}
            />
            <CardContent>
              {loadingBookings ? (
                <Stack spacing={1.5}>
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} height={56} />
                  ))}
                </Stack>
              ) : todaysBookings.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <CheckCircle sx={{ fontSize: 40, color: "action.disabled", mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    {t("dashboard.todayEmpty")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t("dashboard.todayEmptyHint")}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Stack
                    spacing={0}
                    sx={{
                      width: 44,
                      flexShrink: 0,
                      pt: 0.5,
                      "& .hour-slot": { height: PX_PER_HOUR, lineHeight: `${PX_PER_HOUR}px` },
                    }}
                  >
                    {Array.from(
                      { length: TIMELINE_END_HOUR - TIMELINE_START_HOUR },
                      (_, i) => TIMELINE_START_HOUR + i,
                    ).map((h) => (
                      <Typography key={h} variant="caption" color="text.secondary" className="hour-slot">
                        {h === 0
                          ? "12am"
                          : h < 12
                            ? `${h}am`
                            : h === 12
                              ? "12pm"
                              : `${h - 12}pm`}
                      </Typography>
                    ))}
                  </Stack>
                  <Box
                    sx={{
                      flex: 1,
                      position: "relative",
                      borderLeft: 1,
                      borderColor: "divider",
                      minHeight: trackHeight,
                      boxSizing: "border-box",
                    }}
                  >
                    {todaysBookings
                      .sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime())
                      .map((booking) => {
                        const start = new Date(booking.startAt);
                        const endAt = new Date(booking.endAt);
                        const startMin = start.getHours() * 60 + start.getMinutes();
                        const durMin = Math.max((endAt.getTime() - start.getTime()) / 60000, 15);
                        const rangeStart = TIMELINE_START_HOUR * 60;
                        const top = Math.max(0, ((startMin - rangeStart) / timelineMinsTotal) * trackHeight);
                        const height = Math.max((durMin / timelineMinsTotal) * trackHeight, 36);
                        const { lane, maxLanes } = todayScheduleLayout.get(booking.id) ?? {
                          lane: 0,
                          maxLanes: 1,
                        };
                        const inset = CALENDAR_EVENT_INSET_PX;
                        const gap = CALENDAR_LANE_GAP_PX;
                        const totalGaps = Math.max(0, maxLanes - 1) * gap;
                        const left =
                          maxLanes <= 1
                            ? inset
                            : `calc(${inset}px + ${lane} * ((100% - ${2 * inset}px - ${totalGaps}px) / ${maxLanes} + ${gap}px))`;
                        const width =
                          maxLanes <= 1
                            ? `calc(100% - ${2 * inset}px)`
                            : `calc((100% - ${2 * inset}px - ${totalGaps}px) / ${maxLanes})`;
                        const narrow = maxLanes > 1;
                        const staffIdx = Math.max(
                          0,
                          staffList?.findIndex((s) => sameEntityId(s.id, booking.staffId)) ?? 0,
                        );
                        const paletteKey = staffPaletteKeyFromIndex(staffIdx);
                        return (
                          <Box
                            key={booking.id}
                            sx={[
                              calendarBookingEventBaseSx(theme, paletteKey, narrow),
                              {
                                position: "absolute",
                                left,
                                width,
                                top,
                                height,
                                minWidth: 0,
                                cursor: "pointer",
                                pointerEvents: "auto",
                                zIndex: 2,
                              },
                            ]}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedBooking(booking);
                              setDetailOpen(true);
                            }}
                            data-testid={`dashboard-today-event-booking-${booking.id}`}
                          >
                            <Typography
                              variant="caption"
                              fontWeight={600}
                              noWrap
                              display="block"
                              sx={calendarBookingTitleSx(theme, narrow)}
                            >
                              {booking.customer?.firstName} {booking.customer?.lastName}
                            </Typography>
                            <Typography
                              variant="caption"
                              display="block"
                              noWrap
                              sx={calendarBookingSubtitleSx(theme, narrow)}
                            >
                              {resolveOfferingName(booking, servicesCatalog) ?? ""}
                            </Typography>
                          </Box>
                        );
                      })}
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        <Box>
          <Card variant="outlined">
            <CardHeader
              title={t("dashboard.upcomingTitle")}
              subheader={loadingBookings ? undefined : t("dashboard.upcomingSubHeader")}
              action={<AccessTime fontSize="small" color="action" />}
              titleTypographyProps={{ variant: "subtitle1", fontWeight: 600 }}
            />
            <CardContent>
              {loadingBookings ? (
                <Stack spacing={1.5}>
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} height={56} />
                  ))}
                </Stack>
              ) : upcomingBookings.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <CalendarMonth sx={{ fontSize: 40, color: "action.disabled", mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    {t("dashboard.upcomingEmpty")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t("dashboard.upcomingEmptyHint")}
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={1.5}>
                  {upcomingBookings.map((booking) => {
                    const staffName = resolveStaffDisplayName(booking, staffList);
                    return (
                    <Box
                      key={booking.id}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 1,
                        p: 1.5,
                        borderRadius: 1,
                        bgcolor: "action.hover",
                      }}
                      data-testid={`card-booking-${booking.id}`}
                    >
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="caption" color="text.secondary" display="block" fontWeight={600}>
                          {new Date(booking.startAt)
                            .toLocaleDateString("en-GB", { weekday: "short", day: "numeric" })
                            .toUpperCase()}
                        </Typography>
                        <Typography variant="body2" fontWeight={500} noWrap>
                          {booking.customer?.firstName} {booking.customer?.lastName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {resolveOfferingName(booking, servicesCatalog) ?? ""}
                          {staffName ? t("bookingFlow.withStaffLine", { name: staffName }) : ""}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                        <Typography variant="caption" fontWeight={500}>
                          {new Date(booking.startAt).toLocaleTimeString("en-GB", {
                            hour: "numeric",
                            minute: "2-digit",
                          })}
                        </Typography>
                        <Box sx={{ mt: 0.5 }}>
                          <Chip
                            size="small"
                            label={booking.status}
                            color={getStatusChipColor(booking.status)}
                            variant="outlined"
                          />
                        </Box>
                      </Box>
                    </Box>
                  );
                  })}
                </Stack>
              )}
              <Box sx={{ pt: 2, textAlign: "center" }}>
                <Link to="/admin/bookings">
                  <Typography
                    variant="body2"
                    color="primary"
                    sx={{ cursor: "pointer", fontWeight: 600, "&:hover": { textDecoration: "underline" } }}
                  >
                    {t("dashboard.viewAllBookings")} →
                  </Typography>
                </Link>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      <NewBookingDrawer
        open={newBookingOpen}
        onOpenChange={(o) => {
          setNewBookingOpen(o);
          if (!o) setNewBookingPreset(undefined);
        }}
        preset={newBookingPreset}
      />

      <BookingDetailModal
        booking={selectedBooking}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        staffList={staffList}
        onEdit={(b) => {
          setNewBookingPreset(bookingToEditPreset(b));
          setDetailOpen(false);
          setNewBookingOpen(true);
        }}
      />
    </Box>
  );
}
